import React, {useCallback, useEffect, useRef, useState} from 'react'
import {
  Box,
  Input,
  List,
  ListItem,
  Text,
  Button,
  Collapse,
  Flex,
} from '@chakra-ui/react'
import {ChevronDownIcon} from '@chakra-ui/icons'

interface Option {
  key: string
  value: string
  label: string
  meta?: Record<string, unknown>
}

interface Props {
  options: Array<Option>
  selectedValue?: string
  onSelect: (id: string) => void
  showSearch?: boolean
}

export const OmniSelect: React.FC<React.PropsWithChildren<Props>> = ({
  options,
  selectedValue,
  onSelect,
  showSearch = true,
}) => {
  const [query, setQuery] = useState('')
  const [isOpen, setIsOpen] = useState(false)
  const [focusedIndex, setFocusedIndex] = useState(-1)
  const [scrollTop, setScrollTop] = useState(0)

  const containerRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const listRef = useRef<HTMLUListElement>(null)

  // Virtual scrolling constants
  const ITEM_HEIGHT = 40
  const CONTAINER_HEIGHT = 320 // Increased height for better visibility
  const VIRTUALIZATION_THRESHOLD = 500 // Increased threshold for better handling of medium-sized lists
  const BUFFER_SIZE = 5 // Number of items to render above/below the visible area

  // Compute the current selected option based on selectedValue or default to first option
  const selectedOption = React.useMemo(() => {
    if (selectedValue) {
      const found = options.find((option) => option.value === selectedValue)
      return found || null
    }
    // If no selectedValue provided and we have options, use the first one
    return options.length > 0 ? options[0] : null
  }, [selectedValue, options])

  // Automatically select the first option if no selectedValue is provided
  React.useEffect(() => {
    if (!selectedValue && options.length > 0) {
      onSelect(options[0].value)
    }
  }, [selectedValue, options, onSelect])

  // Enhanced filtering that includes meta data
  const filtered = React.useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return options

    return options.filter((option) => {
      // Search in label
      if (option.label.toLowerCase().includes(q)) {
        return true
      }

      // Search in meta data if it exists
      if (option.meta) {
        const metaString = JSON.stringify(option.meta).toLowerCase()
        return metaString.includes(q)
      }

      return false
    })
  }, [options, query])

  // Virtual scrolling calculations
  const shouldVirtualize = filtered.length > VIRTUALIZATION_THRESHOLD
  const visibleCount = Math.ceil(CONTAINER_HEIGHT / ITEM_HEIGHT)
  const startIndex = shouldVirtualize
    ? Math.max(0, Math.floor(scrollTop / ITEM_HEIGHT) - BUFFER_SIZE)
    : 0
  const endIndex = shouldVirtualize
    ? Math.min(
        filtered.length,
        Math.floor(scrollTop / ITEM_HEIGHT) + visibleCount + BUFFER_SIZE,
      )
    : filtered.length
  const visibleItems = shouldVirtualize
    ? filtered.slice(startIndex, endIndex)
    : filtered

  const handleScroll = useCallback(
    (e: React.UIEvent<HTMLUListElement>): void => {
      if (shouldVirtualize) {
        setScrollTop(e.currentTarget.scrollTop)
      }
    },
    [shouldVirtualize],
  )

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent): void => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false)
        setFocusedIndex(-1)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleSelect = useCallback(
    (option: Option): void => {
      onSelect(option.value)
      setIsOpen(false)
      setQuery('')
      setFocusedIndex(-1)
    },
    [onSelect],
  )

  // Keyboard navigation
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent): void => {
      if (!isOpen) {
        if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
          e.preventDefault()
          setIsOpen(true)
          setFocusedIndex(0)
        }
        return
      }

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault()
          setFocusedIndex((prev) => Math.min(prev + 1, filtered.length - 1))
          break
        case 'ArrowUp':
          e.preventDefault()
          setFocusedIndex((prev) => Math.max(prev - 1, 0))
          break
        case 'Enter':
          e.preventDefault()
          if (focusedIndex >= 0 && filtered[focusedIndex]) {
            handleSelect(filtered[focusedIndex])
          }
          break
        case 'Escape':
          e.preventDefault()
          setIsOpen(false)
          setFocusedIndex(-1)
          inputRef.current?.blur()
          break
        default:
          // No action needed for other keys
          break
      }
    },
    [isOpen, focusedIndex, filtered, handleSelect],
  )

  const handleToggle = useCallback((): void => {
    setIsOpen(!isOpen)
    if (!isOpen) {
      setFocusedIndex(0)
      // Focus the search input when opening
      setTimeout(() => inputRef.current?.focus(), 100)
    } else {
      setFocusedIndex(-1)
    }
  }, [isOpen])

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>): void => {
      setQuery(e.target.value)
      setFocusedIndex(0) // Reset focus to first item when searching
    },
    [],
  )

  // Scroll focused item into view (works with virtualization)
  useEffect(() => {
    if (focusedIndex >= 0 && listRef.current) {
      const targetScrollTop = focusedIndex * ITEM_HEIGHT
      const currentScrollTop = listRef.current.scrollTop
      const containerHeight = CONTAINER_HEIGHT
      const buffer = ITEM_HEIGHT * 2 // Add some buffer space

      // Only scroll if the item is outside the visible area (including buffer)
      if (targetScrollTop < currentScrollTop + buffer) {
        // Item is above visible area - scroll up with buffer
        listRef.current.scrollTop = Math.max(0, targetScrollTop - buffer)
      } else if (
        targetScrollTop >
        currentScrollTop + containerHeight - buffer
      ) {
        // Item is below visible area - scroll down with buffer
        listRef.current.scrollTop = Math.min(
          (filtered.length - visibleCount) * ITEM_HEIGHT,
          targetScrollTop - containerHeight + buffer * 2,
        )
      }
    }
  }, [focusedIndex, filtered.length, visibleCount])

  // Handle case with no options
  if (options.length === 0) {
    return <Text>No options</Text>
  }

  // Handle case with single option - no longer needed to call handleSelect directly
  // since the effect above will handle it

  return (
    <Flex
      minW='320px'
      height='100%'
      position='relative'
      ref={containerRef}
      justifyContent='center'
      alignItems='center'>
      {/* Selected value display / trigger */}
      <Button
        w='100%'
        justifyContent='space-between'
        variant='ghost'
        onClick={handleToggle}
        onKeyDown={handleKeyDown}
        rightIcon={<ChevronDownIcon w='24px' h='24px' />}
        fontWeight='normal'
        bg='white'
        _active={{bg: 'transparent'}}
        _focus={{bg: 'transparent'}}
        _hover={{bg: 'transparent'}}>
        <Text
          isTruncated
          textAlign='right'
          width='100%'
          fontSize='sm'
          lineHeight='1rem'>
          {selectedOption ? selectedOption.label : 'No options available'}
        </Text>
      </Button>

      {/* Dropdown content */}
      <Collapse in={isOpen} animateOpacity>
        <Box
          position='absolute'
          top='100%'
          left={0}
          right={0}
          zIndex={1000}
          bg='white'
          border='1px solid #E2E8F0'
          borderRadius='6px'
          boxShadow='lg'
          mt={1}>
          {/* Search input */}
          {showSearch && (
            <Box p={2} borderBottom='1px solid #E2E8F0'>
              <Input
                ref={inputRef}
                placeholder='Search options...'
                value={query}
                onChange={handleSearchChange}
                onKeyDown={handleKeyDown}
                size='sm'
                autoFocus
              />
            </Box>
          )}

          {/* Options list */}
          <List
            ref={listRef}
            maxH={`${CONTAINER_HEIGHT}px`}
            overflowY='auto'
            onScroll={handleScroll}
            position='relative'>
            {shouldVirtualize && (
              <>
                {/* Top spacer for virtual scrolling */}
                <Box h={`${startIndex * ITEM_HEIGHT}px`} />
              </>
            )}

            {filtered.length > 0 ? (
              visibleItems.map((option, index) => {
                const actualIndex = shouldVirtualize
                  ? startIndex + index
                  : index
                return (
                  <ListItem
                    key={option.key}
                    px={3}
                    py={2}
                    h={`${ITEM_HEIGHT}px`}
                    display='flex'
                    alignItems='center'
                    cursor='pointer'
                    bg={
                      actualIndex === focusedIndex ? '#DFE8ED' : 'transparent'
                    }
                    _hover={{background: '#EFF3F6'}}
                    onClick={() => handleSelect(option)}>
                    <Text isTruncated>{option.label}</Text>
                  </ListItem>
                )
              })
            ) : (
              <ListItem px={3} py={2} color='gray.500' fontStyle='italic'>
                No matches found
              </ListItem>
            )}

            {shouldVirtualize && (
              <>
                {/* Bottom spacer for virtual scrolling */}
                <Box h={`${(filtered.length - endIndex) * ITEM_HEIGHT}px`} />
              </>
            )}
          </List>
        </Box>
      </Collapse>
    </Flex>
  )
}
